import { Lead } from '../types';

/**
 * Extracts the latest update timestamp (as a Date object) for a given lead.
 * Checks activityLog timestamps, lastContacted string, and followUpCompletedAt.
 */
export function getLeadLastUpdateDate(lead: Lead): Date | null {
  const dates: number[] = [];

  // Check activityLog entries
  if (lead.activityLog && lead.activityLog.length > 0) {
    for (const log of lead.activityLog) {
      if (log.timestamp) {
        const d = new Date(log.timestamp);
        if (!isNaN(d.getTime())) dates.push(d.getTime());
      }
    }
  }

  // Check lastContacted
  if (lead.lastContacted) {
    const d = new Date(lead.lastContacted);
    if (!isNaN(d.getTime())) dates.push(d.getTime());
  }

  // Check followUpCompletedAt
  if (lead.followUpCompletedAt) {
    const d = new Date(lead.followUpCompletedAt);
    if (!isNaN(d.getTime())) dates.push(d.getTime());
  }

  if (dates.length === 0) return null;
  return new Date(Math.max(...dates));
}

/**
 * Formats the last update date into a human-readable ISO date string (YYYY-MM-DD).
 */
export function formatLastUpdateDate(lead: Lead): string {
  const d = getLeadLastUpdateDate(lead);
  if (!d) return lead.lastContacted || '—';
  return d.toISOString().slice(0, 10);
}

export const UPDATE_GROUP_ORDER = [
  'Today',
  'Yesterday',
  'Last 7 Days',
  'Last 30 Days',
  'Older',
  'No Update Recorded',
];

/**
 * Classifies a lead into a timeframe group based on its last update date.
 */
export function getLeadUpdateGroup(lead: Lead): string {
  const d = getLeadLastUpdateDate(lead);
  if (!d) return 'No Update Recorded';

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterdayStart = todayStart - 24 * 60 * 60 * 1000;
  const sevenDaysAgoStart = todayStart - 6 * 24 * 60 * 60 * 1000;
  const thirtyDaysAgoStart = todayStart - 29 * 24 * 60 * 60 * 1000;

  const time = d.getTime();

  if (time >= todayStart) return 'Today';
  if (time >= yesterdayStart) return 'Yesterday';
  if (time >= sevenDaysAgoStart) return 'Last 7 Days';
  if (time >= thirtyDaysAgoStart) return 'Last 30 Days';
  return 'Older';
}
